import { IApp, IProjectApp, IProjectDeprecated, IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { Sidebar } from "../components/";
import { selectUserMember } from "../global/globalSlice";
import { useAppDispatch } from "../hooks";
import { IRootState } from "../store";
import ErrorBoundary from "../util/ErrorBoundary";
import asyncComponent from "../util/asyncComponent";
import ProjectBreadcrumbs from "./ProjectBreadCrumbs/ProjectBreadcrumbs";
import { logicWhenProjectOpens } from "./actions";
import AppSettingsPage from "./routes/admin/AppSettingsPage";
import MembersPage from "./routes/admin/members/MembersPage";
import { doGetSingleProjectWithAccessResp, doJoinProject } from "./routes/admin/restQueries";
import { isEmailDomainMulticonsult } from "./routes/admin/selectors";
import BatchJobApp from "./routes/batchjob/BatchJobApp";
import BatchJobBoard from "./routes/batchjob/BatchJobBoard";
import ChecklistApp from "./routes/checklist/ChecklistApp";
import ChecklistBoards from "./routes/checklist/ChecklistBoards";
import { TypeOfChecklistEnum } from "./routes/checklist/ChecklistConstants";
import IssueApp from "./routes/issuesApp/IssueApp";
import IssueBoards from "./routes/issuesApp/IssueBoards";
import MultiRegisterIssuesWrapper from "./routes/issuesApp/MultiRegisterIssues/MultiRegisterIssuesWrapper";
import { MeetingApp } from "./routes/meeting/MeetingApp";
import SingleMeeting from "./routes/meeting/SingleMeeting/SingleMeeting";
import ProjectDashboard from "./routes/projectdashboard/ProjectDashboard";
import ReportApp from "./routes/report/ReportApp";
import MainReportPage from "./routes/report/ReportBoard/MainReportPage";
import AppsPage from "./routes/start/AppsPage";

const ProjectApp: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch<{ projectId: string }>();
  const dispatch = useAppDispatch();

  const project = useSelector((s: IRootState) => s.adminReducer.project);
  const user = useSelector((s: IRootState) => s.globalReducer.user as IUserDepricated | null);
  const member = useSelector(selectUserMember);
  const apps = useSelector((s: IRootState) => s.globalReducer.apps as IApp[]);

  const projectId = useMemo(() => {
    // original getProjectId split logic; here we rely on URL directly to stay identical
    const arr = location.pathname.split("/");
    return arr[2];
  }, [location.pathname]);

  const isCollapsed = useCallback((): boolean => {
    const href = window.location.href;
    return href.endsWith("apps") === false || href.endsWith("settings/apps");
  }, []);

  const init = useCallback(() => {
    doGetSingleProjectWithAccessResp(projectId, async (proj: IProjectDeprecated | null, noAccess: boolean) => {
      if (noAccess === true) {
        try {
          const joined = await doJoinProject(projectId);
          if (joined == null) {
            history.push(`/no-access`);
            return;
          }
          // we treat joined as the project
          proj = joined;
        } catch {
          history.push(`/no-access`);
          return;
        }
      }

      const isUserMulticonsult = isEmailDomainMulticonsult(user && (user as IUserDepricated).email);
      const useMCCalls = isUserMulticonsult && process.env.NODE_ENV !== "development";

      // original dispatched action
      dispatch(logicWhenProjectOpens(match.params.projectId, history, !!useMCCalls));
    });
  }, [dispatch, history, match, projectId, user]);

  useEffect(() => {
    init();
  }, [projectId]);

  const queryParams = new URLSearchParams(location.search);
  const isClean = queryParams.get("doksam") === "true";

  // Keep original "match.url" usage; useRouteMatch provides it
  const baseUrl = (match?.url ?? "").replace(/\/$/, "");

  return (
    <div className="mc-grid-template">
      <div style={{ gridArea: "header" }}></div>

      <div className="" style={{ gridArea: "sidebar" }}>
        {!isClean && project != null && <Sidebar isCollapsed={isCollapsed()} />}
      </div>

      {project != null && user != null && (
        <>
          <div style={{ gridArea: "bread" }}>
            <ProjectBreadcrumbs />
          </div>
          <Switch>
            <Route path={`${baseUrl}/apps`}>
              <ErrorBoundary identifier="ProjectApp AppsPage">
                <AppsPage />
              </ErrorBoundary>
            </Route>

            <Route
              path={`${baseUrl}/members`}
              component={() => (
                <ErrorBoundary identifier="ProjectApp MembersPage">
                  <MembersPage projectId={project!._id} />
                </ErrorBoundary>
              )}
            />

            <Route path={`${baseUrl}/settings`}>
              <AppSettingsPage projectId={project!._id} />
            </Route>

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "issues"),
            ) && (
              <Route exact path={`${baseUrl}/issues`}>
                <ErrorBoundary identifier="ProjectApp IssuesApp">
                  <IssueBoards />
                </ErrorBoundary>
              </Route>
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "issues"),
            ) && (
              <Route exact path={`${baseUrl}/issues-multiregister`}>
                <ErrorBoundary identifier="ProjectApp MultiRegisterIssues">
                  <MultiRegisterIssuesWrapper />
                </ErrorBoundary>
              </Route>
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "issues"),
            ) && (
              <Route
                path={`${baseUrl}/issues/:issueBoardId`}
                exact
                render={(props) => (
                  <ErrorBoundary identifier="ProjectApp IssuesBoard">
                    <IssueApp issueBoardId={props.match.params.issueBoardId} />
                  </ErrorBoundary>
                )}
              />
            )}
            <Route
              path={`${baseUrl}/checklist`}
              exact
              component={() => <Redirect to={`${baseUrl}/checklist/${TypeOfChecklistEnum.CHECKLIST}`} />}
            />

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "checklist"),
            ) && (
              <Route
                path={`${baseUrl}/checklist/:typoeofchecklist`}
                render={(props) => (
                  <ErrorBoundary identifier="ProjectApp Checklist">
                    <ChecklistBoards typeOfChecklist={props.match.params.typoeofchecklist as TypeOfChecklistEnum} />
                  </ErrorBoundary>
                )}
                exact
              />
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "checklist"),
            ) && (
              <Route
                path={`${baseUrl}/checklist/:typoeofchecklist/:checklistId/`}
                exact
                render={(props) => (
                  <ErrorBoundary identifier="ProjectApp ChecklistBoards">
                    <ChecklistApp checklistId={props.match.params.checklistId} />
                  </ErrorBoundary>
                )}
              />
            )}

            <Route path={`${baseUrl}/dashboard`} exact component={() => <ProjectDashboard />} />

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "batchjob"),
            ) && (
              <Route
                path={`${baseUrl}/batchjob`}
                exact
                component={() => (
                  <ErrorBoundary identifier="ProjectApp BatchJobApp">
                    <BatchJobApp />
                  </ErrorBoundary>
                )}
              />
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "batchjob"),
            ) && (
              <Route
                path={`${baseUrl}/batchjob/:template/:batchJobId`}
                render={(props) => (
                  <ErrorBoundary identifier="ProjectApp BatchJobBoard">
                    <BatchJobBoard template={props.match.params.template} batchJobId={props.match.params.batchJobId} />
                  </ErrorBoundary>
                )}
              />
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "reports"),
            ) && (
              <Route
                path={`${baseUrl}/reports`}
                exact
                component={() => (
                  <ErrorBoundary identifier="ProjectApp ReportApp">
                    <ReportApp />
                  </ErrorBoundary>
                )}
              />
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "reports"),
            ) && (
              <Route
                path={`${baseUrl}/reports/:reportId`}
                render={(props) => (
                  <ErrorBoundary identifier="ProjectApp MainReportPage">
                    <MainReportPage reportId={props.match.params.reportId} />
                  </ErrorBoundary>
                )}
              />
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "meetings"),
            ) && (
              <Route
                path={`${baseUrl}/meetings`}
                render={() => (
                  <ErrorBoundary identifier="ProjectApp MeetingApp">
                    <MeetingApp />
                  </ErrorBoundary>
                )}
                exact
              />
            )}

            {memberHasAccessToApp(
              member,
              project?.apps,
              apps.find((a) => a.name === "meetings"),
            ) && (
              <Route
                path={`${baseUrl}/meetings/:meetingId`}
                render={(props) => (
                  <ErrorBoundary identifier="ProjectApp SingleMeeting">
                    <SingleMeeting meetingId={props.match.params.meetingId} />
                  </ErrorBoundary>
                )}
                exact
              />
            )}

            <Route component={asyncComponent(() => import("../components/Error404"))} />
          </Switch>
        </>
      )}
    </div>
  );
};

export const memberHasAccessToApp = (member: IProjectMemberApiItem, projectApps: IProjectApp[], app: IApp) => {
  const projectApp = projectApps.find((papp) => papp.app === app?.name);
  if (projectApp == null) {
    return false;
  }
  if (member?.isPassive === true) {
    return false;
  }

  if (member?.group == null) {
    return true;
  }

  if (projectApp.active && (member.group === "INTERNAL" || member.group === "ADMIN")) {
    return true;
  }

  if (projectApp.access.partner && member.group === "PARTNER") {
    return true;
  }

  if (projectApp.access.client && member.group === "CLIENT") {
    return true;
  }
  return false;
};

export default ProjectApp;
